import {Injectable, signal} from '@angular/core';
import {AppConfigService} from "../config/config.service";
import {Router} from "@angular/router";
import {FidelityService} from "../../features/fidelity/fidelity.service";
import {CartService} from "../../features/cart/cart.service";
import {TileDataService} from "../../features/main-screen/tile-data.service";
import {DinerMode} from "../../features/cart/cart.types";
import {AppService} from "./app.service";

/**
 * Represents the possible 'phases' supported by this service.
 * A phase can include multiple pages and steps.
 */
export type NavigationPhase =
  "splashScreen"
  | "language"
  | "configurator"
  | "fidelity-question"
  | "order-type"
  | "main"
  | "checkout"
  | "kiosk-blocked"
  | "error";

/**
 * Service that handles navigation between phases of the order flow.
 * Note: A phase may include multiple screens and interactions. Each phase must handle its own logic outside of this service, this service only handles transitions between phases, not internal transitions between screens.
 * For example, fidelity question may call fidelity scan outside of this service, and the checkout phase has its own logic using the CheckoutService.
 */
@Injectable({
  providedIn: 'root'
})
export class OrderPhasesNavigationService {
  config = AppConfigService.Config;

  /**
   * Represents the current phase.
   * @private
   */
  private currentPhase = signal<NavigationPhase>("splashScreen");

  constructor(private router: Router,
              private fidelityService: FidelityService,
              private cartService: CartService,
              private tileService: TileDataService,
              private appService: AppService) {
  }

  proceedFromSplashScreenPhase(next: "language" | "fidelity") {

    switch (next) {
      //User touched the language button to choose one
      case "language":
        this.navigateToChooseLanguagePhase()
        break;
      //User is proceeding: first step is to ask fidelity if applicable
      case "fidelity":
        this.navigateToFidelityQuestionPhase();
        break;
      default:
        break;
    }
  }

  proceedFromChooseLanguagePhase(next: "splashScreen" | "fidelity" | "configurator") {

    switch (next) {
      //Go back
      case "splashScreen":
        this.navigateToSplashScreenPhase().then();
        break;
      //Proceed to ask fidelity if applicable
      case "fidelity":
        this.navigateToFidelityQuestionPhase();
        break;
      //An operator enters special configurator phase
      case "configurator":
        this.navigateToConfiguratorPhase();
        break;
      default:
        break;
    }
  }

  proceedFromConfiguratorPhase() {
    //Only way to exit from configurator is to reset app by refreshing page
    this.appService.reloadApplication();
  }

  proceedFromFidelityQuestionPhase(next: "order-type") {

    switch (next) {
      //In any case (auth or not), user should proceed to order type question phase
      case "order-type":
        this.navigateToOrderTypeQuestionPhase();
        break;
      default:
        break;
    }
  }

  proceedFromOrderTypePhase(dinerMode: DinerMode) {
    //From order type we can only go to main
    this.navigateToMainPhaseFromOrderType(dinerMode);
  }

  async proceedFromMainPhase(next: "checkout" | "kiosk-blocked" | "splashScreen", kioskBlockedReason: string | undefined = undefined) {

    switch (next) {
      //User is going from cart to check out phase
      case "checkout":
        this.currentPhase.set("checkout");
        this.router.navigate(['/checkout']).then();
        break;
      //An error occurred during checkout in cart. Kiosk is blocked
      case "kiosk-blocked":
        this.navigateToKioskBlockedPhase(kioskBlockedReason);
        break;
      //User has reset an order.
      case "splashScreen":
        await this.navigateToSplashScreenPhase(true);
        break;
      default:
        break;
    }
  }

  async proceedFromCheckoutPhase(next: "main" | "kiosk-blocked" | "splashScreen", kioskBlockedReason: string | undefined = undefined) {

    switch (next) {
      //User went back to cart
      case "main":
        this.currentPhase.set("main");
        this.router.navigate(['cart']).then();
        break;

      //An error occurred. Kiosk is blocked
      case "kiosk-blocked":
        this.navigateToKioskBlockedPhase(kioskBlockedReason);
        break;

      //User has completed order.
      case "splashScreen":
        await this.navigateToSplashScreenPhase(true);
        break;

      default:
        break;
    }
  }


  /**
   * Navigates to the splash screen, with the possibility of performing a state reset.
   * Used by various pages to return to the initial screen.
   * @param shouldReset
   */
  public async navigateToSplashScreenPhase(shouldReset: boolean = false) {
    //Should we return to splash screen by resetting?
    //Reset occurs when a user exits an order, by completing or resetting it.
    if (shouldReset) {
      await this.appService.clearApplicationState().then(() => {
        this.currentPhase.set("splashScreen");
        this.router.navigate(['/']).then();
      });
    } else {
      this.currentPhase.set("splashScreen");
      this.router.navigate(['/']).then();
    }
  }

  /**
   * Navigate to special phase 'Error', only used in case of API errors.
   */
  navigateToErrorPhase(cause: string) {
    this.router.navigate(['error'], {queryParams: {cause: cause}, replaceUrl: true}).then();
  }

  private navigateToKioskBlockedPhase(reason: string | undefined) {
    this.currentPhase.set("kiosk-blocked");
    this.router.navigate(['kiosk-blocked'], {queryParams: {reason: reason}}).then()
  }

  private navigateToConfiguratorPhase() {
    this.currentPhase.set("configurator");
    this.router.navigate(['configurator']).then();
  }

  private navigateToChooseLanguagePhase() {
    this.currentPhase.set("language");
    this.router.navigate(['/choose-language']).then();
  }

  private navigateToFidelityQuestionPhase() {
    //Verify if fidelity is enabled. If not, we should skip fidelity and try to go straight to order type
    if (this.fidelityService.shouldDisplayFidelityQuestion()) {
      this.currentPhase.set("fidelity-question");
      this.router.navigate(['/fidelity', 'question']).then();
    } else {
      this.navigateToOrderTypeQuestionPhase();
    }
  }

  private navigateToOrderTypeQuestionPhase() {
    //Verify enabled service types. If only one is enabled, skip choice and move directly,
    //otherwise go to order type question phase.
    const takeAway = this.config()?.serviceTypes.takeAway;
    const tableService = this.config()?.serviceTypes.tableService;

    if (takeAway?.enabled && !tableService?.enabled) {
      this.navigateToMainPhaseFromOrderType("take_away");

    } else if (tableService?.enabled && !takeAway?.enabled) {
      this.navigateToMainPhaseFromOrderType("restaurant");
    } else {
      this.currentPhase.set("order-type");
      this.router.navigate(['/order-type']).then();
    }
  }

  /**
   * Navigates to main phase after setting up the diner mode and related data.
   * @param dinerMode
   * @private
   */
  private navigateToMainPhaseFromOrderType(dinerMode: DinerMode) {
    this.cartService.setDinerMode(dinerMode);
    this.tileService.clear();
    this.currentPhase.set("main");
    this.router.navigate(['main']).then();
  }

}
